import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { AxiosRequest } from "_services/api";
import { getIdUsuario } from "_services/auth";
import { alertaWarnig } from "components/Alerta";
import { toastErro } from "components/Toast";
import {
  ILoteEstampagem,
  IEstampagemPlacaItem,
  IValidarPlacaResponse,
  IValidarTagResponse,
} from "../../../interfaces/ILoteEstampagem";
import {
  buscarImpressoraPadrao,
  listarImpressoras,
  enviarZpl,
  ZebraDevice,
} from "_services/zebraBrowserPrint/zebraBrowserPrint.service";
import {
  gerarZplEtiquetaProcesso,
  gerarHtmlEtiqueta,
  DadosEtiquetaZpl,
} from "_services/zebraBrowserPrint/zplTemplate";

// const api = AxiosRequest.makeApi("http://localhost:6005/api/v2"); // TODO: retirar
const api = AxiosRequest.makeApi(process.env.REACT_APP_MS_AW);
const apiDefault = AxiosRequest.makeApi();

function normalizarTipo(lote: ILoteEstampagem): ILoteEstampagem {
  return (lote.tipo as string) === '' ? { ...lote, tipo: 'LER_CODIGO_BARRAS' } : lote;
}

export function useGerenciarLote(idLote: number) {
  const navigate = useNavigate();

  const [lote, setLote] = useState<ILoteEstampagem | null>(null);
  const [itens, setItens] = useState<IEstampagemPlacaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingFinalizar, setLoadingFinalizar] = useState(false);
  const [impressora, setImpressora] = useState<ZebraDevice | null>(null);
  const [erroImpressora, setErroImpressora] = useState<string | null>(null);
  const [impressoras, setImpressoras] = useState<ZebraDevice[]>([]);

  const carregarLote = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<{ lote: ILoteEstampagem; itens: IEstampagemPlacaItem[] }>(
        `/lote-emplacamento/${idLote}/resumo`
      );
      setLote(normalizarTipo(data.lote));
      setItens(data.itens);
    } catch (error: any) {
      toastErro(error?.response?.data?.message ?? error?.message ?? "Erro ao carregar lote");
    } finally {
      setLoading(false);
    }
  }, [idLote]);

  const recarregarItens = useCallback(async () => {
    try {
      const { data } = await api.get<IEstampagemPlacaItem[]>(`/lote-emplacamento/${idLote}/item`);
      setItens(data);
    } catch {
      // silencioso — itens continuam exibidos
    }
  }, [idLote]);

  const verificarImpressora = useCallback(async () => {
    try {
      const device = await buscarImpressoraPadrao();
      setImpressora(device);
      setErroImpressora(null);
    } catch (error: any) {
      setImpressora(null);
      setErroImpressora(error?.message ?? "Impressora não encontrada");
    }
  }, []);

  const carregarImpressoras = useCallback(async () => {
    try {
      const lista = await listarImpressoras();
      setImpressoras(lista);
    } catch {
      setImpressoras([]);
    }
  }, []);

  const gerarGuia = useCallback(async (loteParam: ILoteEstampagem) => {
    const { data: linhas } = await api.get<any[]>(`/lote-emplacamento/${loteParam.id}/exportar`);
    const linhasHtml = linhas.map((row) =>
      "<tr><td>" + (row["Placa"] ?? "-") + "</td><td>" + (row["Renavam"] ?? "-") + "</td><td>" + (row["Chassi"] ?? "-") + "</td></tr>"
    ).join("");
    const html = [
      "<!DOCTYPE html><html><head><meta charset='UTF-8'>",
      "<title>Protocolo de Entrega #" + loteParam.id + "</title>",
      "<style>",
      "* { margin: 0; padding: 0; box-sizing: border-box; }",
      "body { font-family: Arial, sans-serif; font-size: 11pt; color: #111; }",
      ".header { position: relative; height: 80px; margin-bottom: 30px; }",
      ".header-badge { position: absolute; top: 0; right: 0; width: 260px; height: 80px; background: #1b3c6e; clip-path: polygon(22% 0, 100% 0, 100% 100%, 0 100%); display: flex; align-items: center; justify-content: flex-end; padding-right: 22px; gap: 10px; color: white; }",
      ".lw-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 3px; width: 24px; height: 24px; }",
      ".lw-grid span { background: white; border-radius: 1px; display: block; }",
      ".lw-name { font-size: 15pt; font-weight: 400; letter-spacing: 1px; }",
      ".lw-name b { font-weight: 900; }",
      "h1 { text-align: center; font-size: 15pt; font-weight: 900; letter-spacing: 2px; margin-bottom: 28px; padding: 0 40px; }",
      ".info { padding: 0 40px; margin-bottom: 20px; line-height: 2; }",
      ".info p { font-size: 11pt; }",
      "body { padding-bottom: 220px; }",
      "table { border-collapse: collapse; margin: 0 40px 20px; width: calc(100% - 80px); }",
      "th, td { border: 1px solid #aaa; padding: 3px 10px; font-size: 10pt; }",
      ".bottom-fixed { position: fixed; bottom: 0; left: 0; right: 0; background: white; }",
      ".sign { padding: 14px 40px 8px; }",
      ".sign p { margin-bottom: 14px; font-size: 10pt; }",
      ".footer-wave { line-height: 0; }",
      ".footer-wave svg { display: block; width: 100%; }",
      ".footer-body { background: #1b3c6e; color: white; padding: 12px 30px; display: flex; justify-content: space-between; align-items: center; }",
      ".f-left { display: flex; align-items: center; gap: 12px; }",
      ".f-qr { width: 56px; height: 56px; border: 1.5px solid rgba(255,255,255,0.5); display: flex; align-items: center; justify-content: center; font-size: 7pt; text-align: center; color: rgba(255,255,255,0.7); }",
      ".f-brand-name { font-size: 11pt; }",
      ".f-brand-name b { font-weight: 900; }",
      ".f-social { display: flex; gap: 5px; margin: 3px 0; }",
      ".f-social span { width: 18px; height: 18px; border: 1px solid white; border-radius: 3px; display: flex; align-items: center; justify-content: center; font-size: 8pt; font-weight: bold; }",
      ".f-linktree { font-size: 7pt; opacity: 0.75; }",
      ".f-right { text-align: right; }",
      ".f-phone { font-size: 13pt; font-weight: bold; margin-bottom: 4px; }",
      ".f-addr { font-size: 9pt; opacity: 0.9; }",
      "@media print { @page { margin: 12mm 15mm 0 15mm; } @page :first { margin: 0; } }",
      "</style></head><body>",
      "<div class='header'>",
      "  <div class='header-badge'>",
      "    <div class='lw-grid'><span></span><span></span><span></span><span></span></div>",
      "    <div class='lw-name'>LW<b>Tecnologia</b></div>",
      "  </div>",
      "</div>",
      "<h1>PROTOCOLO DE ENTREGA DE PLACAS</h1>",
      "<div class='info'>",
      "  <p><b>CLIENTE:</b> " + loteParam.nomeEmpresa + "</p>",
      "  <p><b>QUANTIDADE:</b> " + linhas.length + " placa(s)</p>",
      "  <p><b>ENTREGA:</b> " + (loteParam.localEntrega ?? "\u2014") + "</p>",
      "  <p><b>LOTE:</b> #" + loteParam.id + "</p>",
      "</div>",
      "<table>",
      "  <thead><tr><th>Placa</th><th>Renavam</th><th>Chassi</th></tr></thead>",
      "  <tbody>" + linhasHtml + "</tbody>",
      "</table>",
      "<div class='bottom-fixed'>",
      "  <div class='sign'>",
      "    <p>Curitiba ___/____/___________.</p>",
      "    <p>Hor\u00e1rio:_______:_______.</p>",
      "    <p>Assinatura (Nome leg\u00edvel):_______________________________________________</p>",
      "  </div>",
      "  <div class='footer-wave'>",
      "    <svg viewBox='0 0 1440 50' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='none' height='40'>",
      "      <path d='M0,50 C400,0 1040,0 1440,50 L1440,50 L0,50 Z' fill='#1b3c6e'/>",
      "    </svg>",
      "  </div>",
      "  <div class='footer-body'>",
      "    <div class='f-left'>",
      "      <div>",
      "        <div class='f-brand-name'>LW<b>Tecnologia</b></div>",
      "      </div>",
      "    </div>",
      "    <div class='f-right'>",
      "      <div class='f-phone'>(41) 3501-8315</div>",
      "      <div class='f-addr'>Rua XV de Novembro, 621 3\u00ba Andar<br>Centro, Curitiba/PR</div>",
      "    </div>",
      "  </div>",
      "</div>",
      "<script>window.print();window.close();<\/script>",
      "</body></html>",
    ].join("\n");
    const win = window.open("", "_blank");
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  }, []);

  const finalizarLote = useCallback(async () => {
    if (!lote) { return; }
    const { isConfirmed } = await alertaWarnig(
      "Finalizar estampagem",
      "Deseja finalizar esta estampagem?",
      { showCancelButton: true, confirmButtonText: "Finalizar" }
    );
    if (!isConfirmed) { return; }
    setLoadingFinalizar(true);
    try {
      await api.patch(`/lote-emplacamento/${lote.id}/fechar`);
      const { data } = await api.get<{ lote: ILoteEstampagem; itens: IEstampagemPlacaItem[] }>(
        `/lote-emplacamento/${lote.id}/resumo`
      );
      await gerarGuia(data.lote);
      navigate("/backoffice/estampagem-placa");
    } catch (error: any) {
      toastErro(error?.response?.data?.message ?? error?.message ?? "Erro ao finalizar lote");
    } finally {
      setLoadingFinalizar(false);
    }
  }, [lote, gerarGuia, navigate]);

  const validarPlaca = useCallback(async (placa: string): Promise<IValidarPlacaResponse> => {
    const { data } = await api.get<IValidarPlacaResponse>(`/lote-emplacamento/validar-placa/${placa}`);
    return data;
  }, []);

  const validarTag = useCallback(async (tag: string): Promise<IValidarTagResponse> => {
    const { data } = await api.get<IValidarTagResponse>(`/lote-emplacamento/validar-tag/${tag}`);
    return data;
  }, []);

  const adicionarItem = useCallback(async (dados: { placa: string; idProcesso?: string; tag?: string }): Promise<IEstampagemPlacaItem> => {
    const idUsuario = getIdUsuario();
    const { data } = await api.post<IEstampagemPlacaItem>(`/lote-emplacamento/${idLote}/item`, {
      ...dados,
      idUsuario: Number(idUsuario),
    });
    return data;
  }, [idLote]);

  const adicionarItemPorChassi = useCallback(async (chassi: string): Promise<IEstampagemPlacaItem> => {
    const idUsuario = getIdUsuario();
    const { data } = await api.post<IEstampagemPlacaItem>(`/lote-emplacamento/${idLote}/item-por-chassi`, {
      chassi,
      idUsuario: Number(idUsuario),
    });
    return data;
  }, [idLote]);

  const atualizarItem = useCallback(async (idItem: number, tag: string): Promise<void> => {
    await api.patch(`/lote-emplacamento/${idLote}/item/${idItem}`, { tag });
  }, [idLote]);

  const removerItem = useCallback(async (idItem: number): Promise<void> => {
    await api.delete(`/lote-emplacamento/${idLote}/item/${idItem}`);
  }, [idLote]);

  const exportarLote = useCallback(async (): Promise<void> => {
    if (!lote) { return; }
    const { data } = await api.get<any[]>(`/lote-emplacamento/${lote.id}/exportar`);
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Estampagem');
    XLSX.writeFile(wb, `lote-estampagem-${lote.id}.xlsx`);
  }, [lote]);

  const buscarTagPorPlaca = useCallback(async (placa: string): Promise<string | null> => {
    const { data } = await api.get<{ tag: string | null }>(`/lote-emplacamento/tag-por-placa/${placa}`);
    return data.tag;
  }, []);

  const buscarProcessoPorPlaca = useCallback(async (placa: string): Promise<string | null> => {
    const { data } = await api.get<{ idProcesso: string | null }>(`/lote-emplacamento/processo-por-placa/${placa}`);
    return data.idProcesso;
  }, []);

  const lerPlaca = useCallback(async (imagemBase64: string): Promise<string> => {
    const { data } = await api.post<{ placa: string }>("/impressao-etiqueta/leitura-placa", { imagemBase64 });
    return data.placa;
  }, []);

  const buscarDadosEtiqueta = useCallback(async (placa: string): Promise<DadosEtiquetaZpl> => {
    const { data } = await api.get<DadosEtiquetaZpl>(`/impressao-etiqueta/etiqueta-por-placa/${placa}`);
    return data;
  }, []);

  const imprimirEtiqueta = useCallback(async (dados: DadosEtiquetaZpl): Promise<void> => {
    const zpl = gerarZplEtiquetaProcesso(dados);
    await enviarZpl(zpl, impressora ?? undefined);
  }, [impressora]);

  const imprimirEtiquetaPdf = useCallback(async (dados: DadosEtiquetaZpl): Promise<void> => {
    const html = await gerarHtmlEtiqueta(dados);
    const win = window.open("", "_blank");
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  }, []);

  const atualizarStatusImpressao = useCallback(async (idProcesso: string, status: string, mensagemErro?: string): Promise<void> => {
    await api.patch(`/impressao-etiqueta/${idProcesso}/status`, { status, mensagemErro });
  }, []);

  const abrirProcesso = useCallback(async (idProcesso: string): Promise<void> => {
    try {
      const { data } = await apiDefault.get(`/processos/param/${idProcesso}`);
      window.open(
        process.env.REACT_APP_DESPACHANTE_BASE_URL + "/pages/processo/Processo.php?param=" + data,
        "_blank",
      );
    } catch (error: any) {
      toastErro(error?.response?.data?.message ?? error?.message ?? "Erro ao abrir processo");
    }
  }, []);

  useEffect(() => {
    carregarLote();
    verificarImpressora();
  }, [carregarLote, verificarImpressora]);

  return {
    lote,
    itens,
    loading,
    loadingFinalizar,
    impressora,
    setImpressora,
    erroImpressora,
    impressoras,
    verificarImpressora,
    carregarImpressoras,
    recarregarItens,
    finalizarLote,
    validarPlaca,
    validarTag,
    adicionarItem,
    adicionarItemPorChassi,
    atualizarItem,
    removerItem,
    exportarLote,
    buscarTagPorPlaca,
    buscarProcessoPorPlaca,
    lerPlaca,
    buscarDadosEtiqueta,
    imprimirEtiqueta,
    imprimirEtiquetaPdf,
    atualizarStatusImpressao,
    abrirProcesso,
  };
}
